/**
 * Samen Kopen 2.0 - Group Gift Pool System
 * Organizer creates pool → shares link → contributors pledge → progress tracked
 */
(function () {
  'use strict';

  var CFG = window.__SK_CFG || {};
  /* API: section override > globale window.POPPEDIJN_API_BASE > productie default */
  /* AVG-afweging (2026-06-10): calls naar deze API zijn functioneel-noodzakelijk
     voor een door de gebruiker expliciet gevraagde dienst. Geen tracking of
     profilering; geen consent-gate nodig. Wel vermelden in de privacyverklaring. */
  var API = CFG.apiBase || (typeof window !== 'undefined' && window.POPPEDIJN_API_BASE) || 'https://poppedijn-api-production.up.railway.app';
  var PAGE_URL = CFG.pageUrl || '/pages/samen-kopen';
  var API_TIMEOUT_MS = 8000;

  /* Fetch met timeout - voorkomt eindeloze laad-states bij hangende API */
  function apiFetch(url, opts) {
    return Promise.race([
      fetch(url, opts || {}),
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('TIMEOUT')); }, API_TIMEOUT_MS);
      })
    ]);
  }
  var SHOP = CFG.shopDomain || 'poppedijn.nl';

  var OCCASION_LABELS = {
    verjaardag: '🎂 Verjaardagscadeau',
    geboorte: '👶 Geboortecadeau',
    sinterklaas: '🎅 Sinterklaascadeau',
    kerst: '🎄 Kerstcadeau',
    anders: '🎁 Groepscadeau'
  };

  /* ── DOM refs ───────────────────────────── */
  var $page     = document.querySelector('[data-sk-page]');
  var $home     = document.querySelector('[data-sk-home]');
  var $poolView = document.querySelector('[data-sk-pool]');
  var $poolContent = document.querySelector('[data-sk-pool-content]');
  var $form     = document.querySelector('[data-sk-form]');
  var $myPools  = document.querySelector('[data-sk-my-pools]');
  var $myGrid   = document.querySelector('[data-sk-my-pools-grid]');
  var $toast    = document.querySelector('[data-sk-toast]');

  if (!$page) return;

  /* ── Helpers ─────────────────────────────── */
  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  function formatPrice(n) {
    return '€' + parseFloat(n).toFixed(2).replace('.', ',');
  }

  function formatDate(d) {
    if (!d) return '';
    var dt = new Date(d);
    return dt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function showToast(msg) {
    if (!$toast) return;
    $toast.textContent = msg;
    $toast.classList.add('sk-toast--visible');
    setTimeout(function () { $toast.classList.remove('sk-toast--visible'); }, 2500);
  }

  function copyText(text, cb) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(cb).catch(fallback);
    } else {
      fallback();
    }
    function fallback() {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); cb(); } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  /* ── LocalStorage: my pools ─────────────── */
  var STORAGE_KEY = 'sk_my_pools';
  /* In-memory fallback voor private mode browsers waar localStorage faalt */
  var _memoryPools = null;

  function getMyPools() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw !== null) return JSON.parse(raw) || [];
    } catch (e) {}
    return _memoryPools || [];
  }

  function saveMyPools(pools) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pools));
    } catch (e) {
      _memoryPools = pools;
    }
  }

  function addMyPool(pool) {
    var pools = getMyPools();
    pools.unshift(pool);
    saveMyPools(pools);
  }

  function removeMyPool(shortId) {
    var pools = getMyPools().filter(function (p) { return p.short_id !== shortId; });
    saveMyPools(pools);
  }

  /* ── Product fetching from Shopify ──────── */
  var fetchedProduct = null;

  function extractHandle(url) {
    var m = url.match(/\/products\/([^?#/]+)/);
    return m ? m[1] : null;
  }

  function fetchProduct() {
    var input = document.getElementById('sk-product-url');
    if (!input) return;
    var url = input.value.trim();
    var handle = extractHandle(url);

    if (!handle) {
      showToast('Dat is geen geldige productlink. Gebruik een link zoals: www.poppedijn.nl/products/...');
      return;
    }

    var fetchBtn = document.querySelector('[data-sk-fetch-product]');
    if (fetchBtn) fetchBtn.textContent = 'Laden...';

    fetch('/products/' + handle + '.json')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var p = data.product;
        if (!p) { showToast('Product niet gevonden'); return; }

        fetchedProduct = {
          title: p.title,
          handle: handle,
          url: '/products/' + handle,
          image: p.image ? p.image.src : '',
          price: p.variants && p.variants[0] ? p.variants[0].price : '0'
        };

        /* Show preview */
        var $preview = document.querySelector('[data-sk-product-preview]');
        var $img = document.querySelector('[data-sk-preview-img]');
        var $title = document.querySelector('[data-sk-preview-title]');
        var $price = document.querySelector('[data-sk-preview-price]');

        if ($preview && $img && $title && $price) {
          $img.src = fetchedProduct.image ? fetchedProduct.image.replace(/\.([a-z]+)$/, '_200x200.$1') : '';
          $title.textContent = fetchedProduct.title;
          $price.textContent = formatPrice(fetchedProduct.price);
          $preview.style.display = '';
        }

        /* Auto-fill target amount */
        var $target = document.getElementById('sk-target');
        if ($target && !$target.value) {
          $target.value = parseFloat(fetchedProduct.price).toFixed(2);
        }

        showToast('Product gevonden!');
      })
      .catch(function () {
        showToast('Kon product niet ophalen. Controleer de URL.');
      })
      .finally(function () {
        if (fetchBtn) fetchBtn.textContent = 'Ophalen';
      });
  }

  /* ── Render: My pools sidebar ───────────── */
  function renderMyPools() {
    var pools = getMyPools();
    if (!$myPools || !$myGrid) return;

    if (pools.length === 0) {
      $myPools.style.display = 'none';
      return;
    }

    $myPools.style.display = '';
    var html = '';
    pools.forEach(function (p) {
      var label = OCCASION_LABELS[p.occasion] || '🎁 Groepscadeau';
      html += '<div class="sk-pool-card">' +
        '<div class="sk-pool-card__header">' +
          '<span class="sk-pool-card__occasion">' + esc(label) + '</span>' +
          '<strong class="sk-pool-card__recipient">voor ' + esc(p.recipient_name) + '</strong>' +
        '</div>' +
        (p.product_title ? '<p class="sk-pool-card__product">' + esc(p.product_title) + '</p>' : '') +
        '<div class="sk-pool-card__target">' + formatPrice(p.target_amount) + '</div>' +
        '<div class="sk-pool-card__actions">' +
          '<button class="btn btn--primary btn--sm" data-sk-open-pool="' + esc(p.short_id) + '">Bekijk</button>' +
          '<button class="btn btn--outline btn--sm" data-sk-share-pool="' + esc(p.short_id) + '">Delen</button>' +
        '</div>' +
      '</div>';
    });

    $myGrid.innerHTML = html;
  }

  /* ── Render: Pool detail view ───────────── */
  function renderPool(pool, isOrganizer, pledges) {
    if (!$poolContent) return;

    var pct = pool.target_amount > 0
      ? Math.min(100, Math.round((pool.total_pledged / parseFloat(pool.target_amount)) * 100))
      : 0;
    var label = OCCASION_LABELS[pool.occasion] || '🎁 Groepscadeau';
    var remaining = Math.max(0, parseFloat(pool.target_amount) - pool.total_pledged);

    var html = '';

    /* Pool header */
    html += '<div class="sk-pool__header">' +
      '<span class="sk-badge">' + esc(label) + '</span>' +
      '<h1 class="sk-pool__title">Cadeau voor ' + esc(pool.recipient_name) + '</h1>' +
      '<p class="sk-pool__organizer">Georganiseerd door ' + esc(pool.organizer_name) + '</p>' +
    '</div>';

    /* Product card */
    if (pool.product_title) {
      html += '<div class="sk-pool__product">' +
        (pool.product_image ? '<a href="' + esc(pool.product_url || '#') + '" target="_blank" class="sk-pool__product-img"><img src="' + esc(pool.product_image) + '" alt="' + esc(pool.product_title) + '" width="120" height="120" loading="lazy"></a>' : '') +
        '<div class="sk-pool__product-info">' +
          '<h3>' + esc(pool.product_title) + '</h3>' +
          (pool.product_price ? '<span class="sk-pool__product-price">' + formatPrice(pool.product_price) + '</span>' : '') +
          (pool.product_url ? '<a href="' + esc(pool.product_url) + '" target="_blank" class="sk-pool__product-link">Bekijk in webshop →</a>' : '') +
        '</div>' +
      '</div>';
    }

    /* Progress bar */
    html += '<div class="sk-pool__progress">' +
      '<div class="sk-pool__progress-header">' +
        '<span class="sk-pool__progress-label">' + formatPrice(pool.total_pledged) + ' van ' + formatPrice(pool.target_amount) + ' toegezegd</span>' +
        '<span class="sk-pool__progress-pct">' + pct + '%</span>' +
      '</div>' +
      '<div class="sk-pool__progress-bar">' +
        '<div class="sk-pool__progress-fill" style="width:' + pct + '%"></div>' +
      '</div>' +
      '<div class="sk-pool__progress-meta">' +
        '<span>' + pool.pledge_count + ' ' + (pool.pledge_count === 1 ? 'bijdrage' : 'bijdragen') + '</span>' +
        (remaining > 0 ? '<span>Nog ' + formatPrice(remaining) + ' nodig</span>' : '<span class="sk-pool__progress-done">Doelbedrag bereikt!</span>') +
      '</div>' +
    '</div>';

    /* Message */
    if (pool.message) {
      html += '<div class="sk-pool__message">' +
        '<p>"' + esc(pool.message) + '"</p>' +
      '</div>';
    }

    /* Deadline */
    if (pool.deadline) {
      html += '<div class="sk-pool__deadline">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>' +
        ' Deadline: ' + formatDate(pool.deadline) +
      '</div>';
    }

    /* Payment info */
    if (pool.payment_info) {
      html += '<div class="sk-pool__payment">' +
        '<strong>Hoe bijdragen?</strong>' +
        '<p>' + esc(pool.payment_info) + '</p>' +
      '</div>';
    }

    /* Pledge form (for contributors, only if pool is active) */
    if (pool.status === 'active') {
      html += '<div class="sk-pledge">' +
        '<h3 class="sk-pledge__title">Ik doe mee!</h3>' +
        '<p class="sk-pledge__sub">Geef aan hoeveel je wilt bijdragen. Je bijdrage is alleen zichtbaar voor de organisator.</p>' +
        '<form class="sk-pledge__form" data-sk-pledge-form>' +
          '<div class="sk-pledge__row">' +
            '<div class="sk-pledge__field">' +
              '<label>Jouw naam</label>' +
              '<input type="text" name="contributor_name" required placeholder="Bijv. Jan">' +
            '</div>' +
            '<div class="sk-pledge__field sk-pledge__field--amount">' +
              '<label>Bedrag (€)</label>' +
              '<input type="number" name="amount" required min="1" step="0.01" placeholder="10,00">' +
            '</div>' +
          '</div>' +
          '<div class="sk-pledge__field">' +
            '<label>Berichtje <span class="sk-form__optional">(optioneel)</span></label>' +
            '<input type="text" name="message" placeholder="Bijv. Van Jan & Marie!">' +
          '</div>' +
          '<button type="submit" class="btn btn--primary btn--lg sk-pledge__submit">' +
            '💰 Bijdrage toezeggen' +
          '</button>' +
        '</form>' +
      '</div>';
    }

    /* Organizer section */
    if (isOrganizer) {
      html += '<div class="sk-organizer">' +
        '<h3 class="sk-organizer__title">Organisator-paneel</h3>';

      /* Share buttons */
      html += '<div class="sk-organizer__share">' +
        '<button class="btn btn--primary btn--sm" data-sk-copy-link="' + esc(pool.short_id) + '">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Link kopieren' +
        '</button>' +
        '<button class="btn btn--outline btn--sm" data-sk-share-wa="' + esc(pool.short_id) + '">' +
          'WhatsApp' +
        '</button>' +
      '</div>';

      /* Pledges list */
      if (pledges && pledges.length > 0) {
        html += '<div class="sk-organizer__pledges">' +
          '<h4>Bijdragen (' + pledges.length + ')</h4>' +
          '<div class="sk-pledges-list">';

        pledges.forEach(function (pl) {
          html += '<div class="sk-pledge-item">' +
            '<div class="sk-pledge-item__info">' +
              '<strong>' + esc(pl.contributor_name) + '</strong>' +
              (pl.message ? '<span class="sk-pledge-item__msg">' + esc(pl.message) + '</span>' : '') +
            '</div>' +
            '<span class="sk-pledge-item__amount">' + formatPrice(pl.amount) + '</span>' +
          '</div>';
        });

        html += '</div></div>';
      } else {
        html += '<p class="sk-organizer__empty">Nog geen bijdragen ontvangen. Deel de link om te beginnen!</p>';
      }

      /* Delete button */
      html += '<button class="sk-organizer__delete" data-sk-delete-pool="' + esc(pool.short_id) + '">Groepscadeau verwijderen</button>';

      html += '</div>';
    }

    $poolContent.innerHTML = html;

    /* Animate progress bar */
    setTimeout(function () {
      var fill = $poolContent.querySelector('.sk-pool__progress-fill');
      if (fill) fill.style.width = pct + '%';
    }, 50);
  }

  /* ── API calls ──────────────────────────── */
  function apiCreatePool(data, cb, onError) {
    apiFetch(API + '/api/pools', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(function (r) { return r.json(); })
    .then(cb)
    .catch(function (err) {
      showToast('Fout bij aanmaken: ' + (err.message === 'TIMEOUT' ? 'verzoek duurde te lang' : err.message));
      if (typeof onError === 'function') onError(err);
    });
  }

  function apiGetPool(shortId, cb) {
    apiFetch(API + '/api/pools/' + shortId)
      .then(function (r) {
        if (!r.ok) throw new Error('Niet gevonden');
        return r.json();
      })
      .then(cb)
      .catch(function () { showToast('Groepscadeau niet gevonden'); });
  }

  function apiGetPledges(shortId, token, cb) {
    apiFetch(API + '/api/pools/' + shortId + '/pledges?token=' + encodeURIComponent(token))
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function () { cb([]); });
  }

  function apiAddPledge(shortId, data, cb) {
    apiFetch(API + '/api/pools/' + shortId + '/pledges', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(function (r) { return r.json(); })
    .then(cb)
    .catch(function (err) { showToast('Fout: ' + err.message); });
  }

  function apiDeletePool(shortId, token, cb) {
    apiFetch(API + '/api/pools/' + shortId + '?token=' + encodeURIComponent(token), { method: 'DELETE' })
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function (err) { showToast('Fout: ' + err.message); });
  }

  /* ── Navigation ─────────────────────────── */
  function showHome() {
    $home.style.display = '';
    $poolView.style.display = 'none';
    window.history.replaceState(null, '', PAGE_URL);
    renderMyPools();
  }

  function showPool(shortId) {
    $home.style.display = 'none';
    $poolView.style.display = '';
    $poolContent.innerHTML = '<p style="text-align:center;padding:2rem;">Laden...</p>';

    /* Check if we're the organizer */
    var myPools = getMyPools();
    var myPool = myPools.filter(function (p) { return p.short_id === shortId; })[0];
    var isOrganizer = !!myPool;
    var token = myPool ? myPool.organizer_token : null;

    window.history.replaceState(null, '', PAGE_URL + '?pool=' + shortId);

    apiGetPool(shortId, function (pool) {
      if (pool.error) {
        $poolContent.innerHTML = '<p style="text-align:center;padding:2rem;">Groepscadeau niet gevonden.</p>';
        return;
      }

      if (isOrganizer && token) {
        apiGetPledges(shortId, token, function (pledges) {
          renderPool(pool, true, pledges);
          bindPoolEvents(pool, token);
        });
      } else {
        renderPool(pool, false, []);
        bindPoolEvents(pool, null);
      }
    });
  }

  function getPoolLink(shortId) {
    return 'https://' + SHOP + PAGE_URL + '?pool=' + shortId;
  }

  /* ── Event binding for pool view ────────── */
  function bindPoolEvents(pool, token) {
    /* Pledge form */
    var pledgeForm = $poolContent.querySelector('[data-sk-pledge-form]');
    if (pledgeForm) {
      pledgeForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var fd = new FormData(pledgeForm);
        var data = {
          contributor_name: fd.get('contributor_name'),
          amount: parseFloat(fd.get('amount')),
          message: fd.get('message') || ''
        };

        var btn = pledgeForm.querySelector('.sk-pledge__submit');
        if (btn) btn.textContent = 'Bezig...';

        apiAddPledge(pool.short_id, data, function (result) {
          if (result.error) {
            showToast(result.error);
            if (btn) btn.textContent = '💰 Bijdrage toezeggen';
            return;
          }
          showToast('Bedankt voor je bijdrage!');
          /* Refresh the pool view */
          showPool(pool.short_id);
        });
      });
    }

    /* Copy link */
    var copyBtn = $poolContent.querySelector('[data-sk-copy-link]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var link = getPoolLink(pool.short_id);
        copyText(link, function () {
          showToast('Link gekopieerd!');
        });
      });
    }

    /* WhatsApp share */
    var waBtn = $poolContent.querySelector('[data-sk-share-wa]');
    if (waBtn) {
      waBtn.addEventListener('click', function () {
        var label = OCCASION_LABELS[pool.occasion] || 'Groepscadeau';
        var text = label + ' voor ' + pool.recipient_name + '!\n\n';
        if (pool.message) text += pool.message + '\n\n';
        text += 'Doe je mee? Klik hier:\n' + getPoolLink(pool.short_id);
        window.open('https://wa.me/?text=' + encodeURIComponent(text), '_blank');
      });
    }

    /* Delete pool */
    var deleteBtn = $poolContent.querySelector('[data-sk-delete-pool]');
    if (deleteBtn && token) {
      deleteBtn.addEventListener('click', function () {
        if (!confirm('Weet je zeker dat je dit groepscadeau wilt verwijderen? Dit kan niet ongedaan worden.')) return;
        apiDeletePool(pool.short_id, token, function () {
          removeMyPool(pool.short_id);
          showToast('Groepscadeau verwijderd');
          showHome();
        });
      });
    }
  }

  /* ── Form: Create pool ──────────────────── */
  if ($form) {
    $form.addEventListener('submit', function (e) {
      e.preventDefault();

      var fd = new FormData($form);
      var data = {
        organizer_name: fd.get('organizer_name'),
        recipient_name: fd.get('recipient_name'),
        occasion: fd.get('occasion') || 'verjaardag',
        message: fd.get('message') || '',
        product_url: fd.get('product_url') || '',
        target_amount: parseFloat(fd.get('target_amount')),
        deadline: fd.get('deadline') || null,
        payment_info: fd.get('payment_info') || ''
      };

      /* Add fetched product info */
      if (fetchedProduct) {
        data.product_title = fetchedProduct.title;
        data.product_image = fetchedProduct.image;
        data.product_price = parseFloat(fetchedProduct.price);
        data.product_handle = fetchedProduct.handle;
        data.product_url = 'https://' + SHOP + fetchedProduct.url;
      }

      var btn = document.querySelector('[data-sk-submit]');
      var btnOriginalHTML = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Aanmaken...'; }

      function restoreButton() {
        if (btn) { btn.disabled = false; btn.innerHTML = btnOriginalHTML; }
      }

      apiCreatePool(data, function (result) {
        restoreButton();

        if (result.error) {
          showToast(result.error);
          return;
        }
        if (!result.short_id) {
          showToast('Aanmaken mislukt, probeer het opnieuw');
          return;
        }

        /* Save to localStorage */
        addMyPool({
          short_id: result.short_id,
          organizer_token: result.organizer_token,
          recipient_name: data.recipient_name,
          occasion: data.occasion,
          product_title: data.product_title || '',
          target_amount: data.target_amount,
          created_at: result.created_at
        });

        showToast('Groepscadeau aangemaakt!');
        $form.reset();
        fetchedProduct = null;
        var $preview = document.querySelector('[data-sk-product-preview]');
        if ($preview) $preview.style.display = 'none';

        /* Navigate to pool view */
        showPool(result.short_id);
      }, restoreButton);
    });
  }

  /* ── Global click handler ───────────────── */
  $page.addEventListener('click', function (e) {
    /* Back button */
    if (e.target.closest('[data-sk-back]')) {
      e.preventDefault();
      showHome();
      return;
    }

    /* Open pool */
    var openBtn = e.target.closest('[data-sk-open-pool]');
    if (openBtn) {
      e.preventDefault();
      showPool(openBtn.dataset.skOpenPool);
      return;
    }

    /* Share pool from my-pools card */
    var shareBtn = e.target.closest('[data-sk-share-pool]');
    if (shareBtn) {
      e.preventDefault();
      var link = getPoolLink(shareBtn.dataset.skSharePool);
      copyText(link, function () { showToast('Link gekopieerd!'); });
      return;
    }

    /* Fetch product */
    if (e.target.closest('[data-sk-fetch-product]')) {
      e.preventDefault();
      fetchProduct();
      return;
    }

    /* Clear product */
    if (e.target.closest('[data-sk-clear-product]')) {
      e.preventDefault();
      fetchedProduct = null;
      var $preview = document.querySelector('[data-sk-product-preview]');
      if ($preview) $preview.style.display = 'none';
      document.getElementById('sk-product-url').value = '';
      return;
    }
  });

  /* ── Auto-fetch product on URL paste ────── */
  var productInput = document.getElementById('sk-product-url');
  if (productInput) {
    productInput.addEventListener('paste', function () {
      setTimeout(fetchProduct, 100);
    });
  }

  /* ── Default deadline: 2 weeks ──────────── */
  var deadlineInput = document.getElementById('sk-deadline');
  if (deadlineInput) {
    var twoWeeks = new Date();
    twoWeeks.setDate(twoWeeks.getDate() + 14);
    deadlineInput.value = twoWeeks.toISOString().split('T')[0];
    deadlineInput.min = new Date().toISOString().split('T')[0];
  }

  /* ── Init: check URL for pool param ─────── */
  var urlParams = new URLSearchParams(window.location.search);
  var poolParam = urlParams.get('pool');

  if (poolParam) {
    showPool(poolParam);
  } else {
    renderMyPools();
  }

})();
